import { useEffect, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';

const labels = {
  native_active: 'Abonnement BEQUALITY actif',
  native_cancelled: 'Abonnement résilié, accès conservé jusqu’à son échéance',
  payment_pending: 'Souscription ou paiement en attente dans BEQUALITY',
  payment_required: 'Paiement à régulariser dans BEQUALITY',
  trial: 'Essai BEQUALITY en cours',
  manual_access: 'Accès de démonstration ou accordé manuellement',
  ended: 'Abonnement BEQUALITY terminé',
  none: 'Aucun abonnement enregistré dans BEQUALITY',
  unknown: 'Situation commerciale à vérifier dans BEQUALITY',
};
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const isoDate = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
export function validBequalitySummary(value, linkId) {
  if (!uuid.test(linkId || '') || value?.schemaVersion !== 1 || !['granted', 'required', 'unavailable'].includes(value.permission)) return false;
  if (value.permission !== 'granted') return exactKeys(value, ['schemaVersion', 'linkId', 'permission']) && (value.linkId === linkId || value.linkId === null);
  if (!exactKeys(value, ['schemaVersion', 'linkId', 'permission', 'summary']) || value.linkId !== linkId) return false;
  const summary = value.summary;
  return exactKeys(summary, ['state', 'managedByParent', 'checkedAt', 'providerVerifiedAt'])
    && Object.prototype.hasOwnProperty.call(labels, summary.state) && typeof summary.managedByParent === 'boolean'
    && isoDate(summary.checkedAt) && (summary.providerVerifiedAt === null || (isoDate(summary.providerVerifiedAt) && summary.providerVerifiedAt <= summary.checkedAt));
}
function accountId() {
  try { return String(JSON.parse(localStorage.getItem('kioskAccount') || 'null')?.id || ''); } catch (_) { return ''; }
}
const readSession = () => ({ token: getStoredAuthToken(), accountId: accountId() });
const sameSession = session => Boolean(session.token && session.accountId) && getStoredAuthToken() === session.token && accountId() === session.accountId;
const formatDate = value => new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
const sessionMessage = 'Votre session a changé. Rechargez Mes options avant de consulter cet abonnement.';

function useBequalityRead(linkId, path, validate) {
  const [session] = useState(readSession);
  const [changed, setChanged] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const controller = useRef(null);
  const currentLink = useRef(linkId);
  currentLink.current = linkId;
  const locked = changed || !sameSession(session);
  useEffect(() => {
    const check = () => {
      if (!sameSession(session)) { controller.current?.abort(); setChanged(true); setResult(null); setBusy(false); }
    };
    window.addEventListener('storage', check); window.addEventListener('focus', check);
    const timer = window.setInterval(check, 250);
    return () => { controller.current?.abort(); window.clearInterval(timer); window.removeEventListener('storage', check); window.removeEventListener('focus', check); };
  }, [session]);
  useEffect(() => {
    setResult(null); setBusy(false);
    return () => controller.current?.abort();
  }, [linkId]);
  async function load() {
    controller.current?.abort();
    const pending = new AbortController(); controller.current = pending;
    const isCurrent = () => !pending.signal.aborted && controller.current === pending && currentLink.current === linkId;
    setResult(null); setBusy(true);
    try {
      if (!sameSession(session) || changed) throw new Error('session');
      const response = await fetch(`${API_URL}/api/admin/integrations/bequality/${path}`, {
        headers: buildKioskAuthHeaders(), cache: 'no-store', signal: pending.signal,
      });
      if (!isCurrent()) return;
      if (!sameSession(session)) throw new Error('session');
      if (!response.ok) throw new Error('unavailable');
      const data = await response.json();
      if (!isCurrent()) return;
      if (!sameSession(session)) throw new Error('session');
      if (!validate(data, linkId)) throw new Error('unavailable');
      setResult({ linkId, data });
    } catch (_) {
      if (!isCurrent()) return;
      if (!sameSession(session)) { setChanged(true); setResult(null); }
      else setResult({ linkId, data: { permission: 'unavailable' } });
    } finally { if (isCurrent()) setBusy(false); }
  }
  const data = !locked && result?.linkId === linkId ? result.data : null;
  return { data, locked, busy, load };
}

export default function BequalitySubscriptionSummary({ linkId }) {
  const { data, locked, busy, load } = useBequalityRead(linkId, 'link/billing-status', validBequalitySummary);
  if (!uuid.test(linkId || '')) return null;
  const summary = data?.permission === 'granted' ? data.summary : null;
  return <div className="bequality-subscription-summary" role="group" aria-label="Abonnement BEQUALITY">
    <h3>Votre abonnement BEQUALITY</h3>
    <p>Consultez sa situation ici après avoir autorisé cette lecture dans BEQUALITY.</p>
    {locked && <p className="bequality-link-error" role="alert">{sessionMessage}</p>}
    {busy && !locked && <p role="status">Consultation de BEQUALITY…</p>}
    {data?.permission === 'required' && <p role="status">Dans BEQUALITY, ouvrez la liaison de cet établissement et autorisez le partage de l’état de votre abonnement. Revenez ensuite ici et actualisez.</p>}
    {data?.permission === 'unavailable' && <p className="bequality-link-error" role="alert">Le statut de l’abonnement ne peut pas être confirmé. Réessayez ou consultez votre compte BEQUALITY.</p>}
    {summary && <div role="status">
      <p><strong>{labels[summary.state]}</strong></p>
      {summary.managedByParent && <p>Abonnement géré par le compte principal du groupe.</p>}
      <p>Données BEQUALITY consultées le {formatDate(summary.checkedAt)}.
        {summary.providerVerifiedAt ? ` Dernière confirmation fournisseur enregistrée le ${formatDate(summary.providerVerifiedAt)}.` : ' Aucune confirmation fournisseur disponible dans ce résumé.'}</p>
      <p>Ce résumé utilise les données enregistrées dans BEQUALITY. Il ne consulte pas PayPal en temps réel.</p>
    </div>}
    <button type="button" disabled={busy || locked} onClick={load}>{data ? 'Actualiser l’abonnement BEQUALITY' : 'Voir mon abonnement BEQUALITY'}</button>
    {!locked && <a className="product-options-open" href="https://bequality.fr/app/beresto-link.html" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Gérer l’autorisation dans BEQUALITY <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    <p>Vos contrats natifs restent gérés dans BEQUALITY. Un achat central se gère dans sa boutique BERESTO. Cette lecture n’autorise aucun achat, transfert d’abonnement ou accès aux données HACCP.</p>
  </div>;
}

const centralLabels = {
  not_purchased: 'Aucun achat BEQUALITY enregistré dans la boutique BERESTO pour cet établissement.',
  reservation_pending: 'Réservation BEQUALITY en cours de vérification.',
  payment_pending: 'Paiement BEQUALITY en attente.',
  activation_pending: 'Paiement reçu, activation BEQUALITY en cours.',
  active: 'Abonnement BEQUALITY actif depuis la boutique BERESTO.',
  suspended: 'Accès BEQUALITY suspendu. Consultez votre abonnement en boutique.',
  ended: 'Abonnement BEQUALITY terminé.',
  released: 'Demande BEQUALITY clôturée sans abonnement actif.',
};
export function validBequalityCommercial(value) {
  return exactKeys(value, ['schemaVersion', 'state', 'paidUntil', 'cancelAtPeriodEnd', 'brand']) && value.schemaVersion === 1
    && Object.prototype.hasOwnProperty.call(centralLabels, value.state)
    && (value.paidUntil === null || isoDate(value.paidUntil)) && typeof value.cancelAtPeriodEnd === 'boolean'
    && (value.state === 'not_purchased' ? value.brand === null : value.brand === 'beresto');
}
export function BequalityCentralBilling({ linkId }) {
  const { data, locked, busy, load } = useBequalityRead(linkId, 'commercial', validBequalityCommercial);
  if (!uuid.test(linkId || '')) return null;
  const unavailable = data?.permission === 'unavailable';
  const canPrepare = data && ['not_purchased', 'ended', 'released'].includes(data.state);
  return <div className="bequality-subscription-summary" role="group" aria-label="BEQUALITY depuis la boutique BERESTO">
    <h3>BEQUALITY depuis la boutique BERESTO</h3>
    <p>49 € HT par mois pour un établissement BEQUALITY autonome existant. Cette offre concerne les comptes récemment ouverts avec leur essai natif de 14 jours, sans contrat existant. Votre éligibilité sera vérifiée avant le paiement.</p>
    <p>Reliez votre établissement puis autorisez séparément les nouveaux achats dans BEQUALITY. Cette autorisation ne déclenche aucun paiement.</p>
    {locked && <p className="bequality-link-error" role="alert">{sessionMessage}</p>}
    {busy && !locked && <p role="status">Vérification de votre achat BEQUALITY…</p>}
    {unavailable && <p className="bequality-link-error" role="alert">La situation de votre achat ne peut pas être vérifiée. Consultez BEQUALITY et réessayez avant de préparer une nouvelle souscription.</p>}
    {data && !unavailable && <div role="status"><p>{centralLabels[data.state]}</p>
      {data.paidUntil && <p>Période payée jusqu’au {formatDate(data.paidUntil)}.</p>}
      {data.cancelAtPeriodEnd && <p>Le renouvellement est arrêté à la fin de la période payée.</p>}
    </div>}
    <button type="button" disabled={busy || locked} onClick={load}>Vérifier mon achat BEQUALITY en boutique</button>
    {!locked && <a className="product-options-open" href="https://bequality.fr/app/beresto-link.html" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Autoriser les nouveaux achats dans BEQUALITY <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    {canPrepare && <><p>L’absence d’achat dans cette boutique ne confirme pas votre éligibilité. Connectez-vous à votre compte boutique BERESTO, puis sélectionnez cet établissement déjà relié. Aucun nouveau compte BEQUALITY ne sera créé.</p><a className="product-options-open" href="https://beresto.fr/boutique.html?item=bequality#product-bequality" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Préparer mon achat BEQUALITY <span className="product-options-sr-only">(nouvel onglet)</span></a></>}
    {data?.state === 'active' && <a className="product-options-open" href="https://bequality.fr/app" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Ouvrir BEQUALITY avec ma connexion habituelle <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    {data?.brand === 'beresto' && <a className="product-options-open" href="https://beresto.fr/boutique.html#mon-compte" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Gérer cet achat dans la boutique BERESTO <span className="product-options-sr-only">(nouvel onglet)</span></a>}
    <p>Vos contrats BEQUALITY existants, notamment PayPal, restent gérés dans BEQUALITY. Si leur situation doit être clarifiée, vérifiez votre compte BEQUALITY avant tout nouvel achat. Cet achat ne connecte pas automatiquement les applications et ne partage pas les données HACCP.</p>
  </div>;
}
